import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

// Trang super-admin: quản lý toàn bộ cửa hàng thuê bao.
// Chỉ role 'super_admin' truy cập được (RLS cho phép đọc/ghi subscriptions).

interface ShopRow {
  id: string;
  owner_id: string;
  name: string;
  phone: string;
  created_at: string;
}

interface ProfileRow {
  id: string;
  display_name: string;
  email: string;
}

interface SubRow {
  id: string;
  shop_id: string;
  plan: string;
  expires_at: string;
  status: string;
}

interface ShopView {
  id: string;
  name: string;
  phone: string;
  ownerName: string;
  ownerEmail: string;
  createdAt: string;
  expiresAt: string | null;
  daysLeft: number | null;
  state: 'active' | 'expiring' | 'expired';
}

function daysUntil(iso: string) {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

export default function AdminShopsPage() {
  const { isSuperAdmin, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ShopView[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [renewTarget, setRenewTarget] = useState<ShopView | null>(null);
  const [plan, setPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: shops }, { data: profiles }, { data: subs }] = await Promise.all([
      supabase.from('shops').select('id, owner_id, name, phone, created_at').order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, display_name, email'),
      supabase.from('subscriptions').select('id, shop_id, plan, expires_at, status'),
    ]);

    const profileMap = new Map((profiles || []).map((p: ProfileRow) => [p.id, p]));
    const activeSubs = (subs || []).filter((s: SubRow) => s.status === 'active');
    const expiryMap = new Map<string, string>();
    for (const s of activeSubs as SubRow[]) {
      const cur = expiryMap.get(s.shop_id);
      if (!cur || s.expires_at > cur) expiryMap.set(s.shop_id, s.expires_at);
    }

    setRows(
      ((shops || []) as ShopRow[]).map((s) => {
        const owner = profileMap.get(s.owner_id);
        const exp = expiryMap.get(s.id) || null;
        const days = exp ? daysUntil(exp) : null;
        return {
          id: s.id,
          name: s.name || '(chưa đặt tên)',
          phone: s.phone,
          ownerName: owner?.display_name || '',
          ownerEmail: owner?.email || '',
          createdAt: s.created_at,
          expiresAt: exp,
          daysLeft: days,
          state: days === null || days <= 0 ? 'expired' : days <= 7 ? 'expiring' : 'active',
        };
      })
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!authLoading && isSuperAdmin) load();
  }, [authLoading, isSuperAdmin, load]);

  useEffect(() => {
    if (!authLoading && !isSuperAdmin) navigate('/', { replace: true });
  }, [authLoading, isSuperAdmin, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.ownerEmail, r.ownerName, r.phone].some((v) => v.toLowerCase().includes(q))
    );
  }, [rows, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.state === 'active').length,
    expiring: rows.filter((r) => r.state === 'expiring').length,
    expired: rows.filter((r) => r.state === 'expired').length,
  }), [rows]);

  const renew = async () => {
    if (!renewTarget) return;
    setSaving(true);
    // Gia hạn từ hạn hiện tại nếu còn, không thì từ hôm nay
    const base = renewTarget.expiresAt && daysUntil(renewTarget.expiresAt) > 0
      ? new Date(renewTarget.expiresAt)
      : new Date();
    const months = plan === 'yearly' ? 12 : 1;
    const expires = new Date(base);
    expires.setMonth(expires.getMonth() + months);

    const { error } = await supabase.from('subscriptions').insert({
      shop_id: renewTarget.id,
      plan,
      started_at: new Date().toISOString(),
      expires_at: expires.toISOString(),
      status: 'active',
      note: note.trim(),
      created_by: user?.id,
    });
    setSaving(false);
    if (error) {
      alert(`Lỗi gia hạn: ${error.message}`);
      return;
    }
    setRenewTarget(null);
    setNote('');
    load();
  };

  const lock = async (row: ShopView) => {
    if (!confirm(`Khóa thuê bao của "${row.name}"? Tiệm sẽ chỉ xem được dữ liệu.`)) return;
    await supabase
      .from('subscriptions')
      .update({ status: 'cancelled' })
      .eq('shop_id', row.id)
      .eq('status', 'active');
    load();
  };

  const badge = (r: ShopView) => {
    if (r.state === 'active')
      return <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-semibold">Hoạt động</span>;
    if (r.state === 'expiring')
      return <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-xs font-semibold">Còn {r.daysLeft} ngày</span>;
    return <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-500 text-xs font-semibold">Hết hạn</span>;
  };

  const statCards = [
    { label: 'Tổng tiệm', value: stats.total, icon: 'ri-store-2-line', color: 'text-[#1a2332]' },
    { label: 'Đang hoạt động', value: stats.active, icon: 'ri-checkbox-circle-line', color: 'text-emerald-500' },
    { label: 'Sắp hết hạn', value: stats.expiring, icon: 'ri-time-line', color: 'text-amber-500' },
    { label: 'Hết hạn', value: stats.expired, icon: 'ri-error-warning-line', color: 'text-red-500' },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl p-4 flex items-center gap-3">
            <i className={`${c.icon} text-2xl ${c.color}`}></i>
            <div>
              <p className="text-2xl font-bold text-[#1a2332]">{c.value}</p>
              <p className="text-xs text-gray-400">{c.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl p-4">
        <div className="relative mb-4">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="Tìm theo tên tiệm, chủ tiệm, email, SĐT..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b]"
          />
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            <i className="ri-loader-4-line animate-spin text-xl"></i>
            <p className="mt-2">Đang tải...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">Chưa có cửa hàng nào</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                  <th className="pb-3 font-medium">Cửa hàng</th>
                  <th className="pb-3 font-medium">Chủ tiệm</th>
                  <th className="pb-3 font-medium">Ngày tạo</th>
                  <th className="pb-3 font-medium">Hạn dùng</th>
                  <th className="pb-3 font-medium">Trạng thái</th>
                  <th className="pb-3 font-medium text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                    <td className="py-3 pr-4">
                      <p className="font-semibold text-gray-800">{r.name}</p>
                      <p className="text-xs text-gray-400">{r.phone}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-gray-700">{r.ownerName || '—'}</p>
                      <p className="text-xs text-gray-400">{r.ownerEmail}</p>
                    </td>
                    <td className="py-3 pr-4 text-gray-500 text-xs">
                      {new Date(r.createdAt).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="py-3 pr-4 text-gray-700">
                      {r.expiresAt ? new Date(r.expiresAt).toLocaleDateString('vi-VN') : '—'}
                    </td>
                    <td className="py-3 pr-4">{badge(r)}</td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => { setRenewTarget(r); setPlan('monthly'); }}
                        className="px-3 py-1.5 bg-[#f59e0b] hover:bg-[#d97706] text-white text-xs font-semibold rounded-lg mr-2 cursor-pointer"
                      >
                        Gia hạn
                      </button>
                      {r.state !== 'expired' && (
                        <button
                          onClick={() => lock(r)}
                          className="px-3 py-1.5 bg-gray-100 hover:bg-red-50 text-gray-500 hover:text-red-500 text-xs font-semibold rounded-lg cursor-pointer"
                        >
                          Khóa
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal gia hạn */}
      {renewTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setRenewTarget(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-[#1a2332] mb-1">Gia hạn — {renewTarget.name}</h3>
            <p className="text-xs text-gray-400 mb-4">
              Hạn hiện tại: {renewTarget.expiresAt ? new Date(renewTarget.expiresAt).toLocaleDateString('vi-VN') : 'chưa có'}
            </p>
            <div className="flex gap-2 mb-4">
              {(['monthly', 'yearly'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPlan(p)}
                  className={`flex-1 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors ${
                    plan === p ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {p === 'monthly' ? '+1 tháng' : '+12 tháng'}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Ghi chú (VD: đã nhận ck 200k)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm mb-4 focus:outline-none focus:border-[#f59e0b]"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setRenewTarget(null)}
                className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold cursor-pointer"
              >
                Huỷ
              </button>
              <button
                onClick={renew}
                disabled={saving}
                className="flex-1 py-2.5 bg-[#f59e0b] hover:bg-[#d97706] disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer"
              >
                {saving ? 'Đang lưu...' : 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
